import { TrendingUp, TrendingDown, Sparkles, Lightbulb, Target } from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from 'recharts';

const revenueTrend = [
  { month: 'Oct', revenue: 22400, profit: 3800 },
  { month: 'Nov', revenue: 24100, profit: 4200 },
  { month: 'Dec', revenue: 28900, profit: 5400 },
  { month: 'Jan', revenue: 21200, profit: 3100 },
  { month: 'Feb', revenue: 25600, profit: 4500 },
  { month: 'Mar', revenue: 27800, profit: 5100 },
];

const expenses = [
  { name: 'Wages & Super', amount: 142000, color: '#a78bfa' },
  { name: 'Rent', amount: 36000, color: '#f0abfc' },
  { name: 'Subscriptions', amount: 18400, color: '#fcd34d' },
  { name: 'Motor Vehicle', amount: 12200, color: '#67e8f9' },
  { name: 'Marketing', amount: 9800, color: '#86efac' },
];

const revenueMix = [
  { name: 'Residential', value: 142000, color: '#a78bfa' },
  { name: 'Commercial', value: 88000, color: '#f0abfc' },
  { name: 'Repairs', value: 38500, color: '#fcd34d' },
  { name: 'Other', value: 16000, color: '#67e8f9' },
];

const benchmarks = [
  { metric: 'Profit margin', you: 17.0, industry: 12.4 },
  { metric: 'Wages / revenue', you: 49.8, industry: 54.1 },
  { metric: 'Rent / revenue', you: 12.6, industry: 14.8 },
];

const cashGauge = [{ name: 'cash', value: 72, fill: 'url(#cashGrad)' }];

const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(10,10,20,0.95)',
  fontSize: 12,
  color: 'white',
  backdropFilter: 'blur(12px)',
};

export default function InsightsPageV2() {
  return (
    <div className="space-y-4 pt-4">
      {/* Editorial heading */}
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/40">Issue 04 · April</p>
        <h1 className="mt-1 text-3xl font-black tracking-tighter">The Pulse</h1>
      </div>

      {/* Hero stat block - magazine cover */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-violet-600/30 via-fuchsia-500/20 to-transparent p-6">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-fuchsia-500/30 blur-3xl" />
        <div className="relative">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-white/60">Headline</p>
          <h2 className="mt-3 max-w-xs text-2xl font-bold leading-tight tracking-tight">
            Profit margin <span className="text-fuchsia-300">37% above</span> industry average
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            You're earning 17¢ on every dollar - peers manage just 12¢. Three drivers below.
          </p>
        </div>
      </div>

      {/* Bento: Revenue + Cashflow side by side */}
      <div className="grid grid-cols-2 gap-3">
        {/* Revenue trend with gradient area */}
        <div className="col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Revenue · 6 mo</p>
              <p className="mt-1 text-2xl font-black tracking-tighter">$150,000</p>
            </div>
            <div className="text-right">
              <p className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                <TrendingUp className="h-2.5 w-2.5" /> +24%
              </p>
            </div>
          </div>
          <div className="mt-4 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTrend}>
                <defs>
                  <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="profArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f0abfc" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#f0abfc" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
                <YAxis hide />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} />
                <Area type="monotone" dataKey="revenue" stroke="#c4b5fd" strokeWidth={2} fill="url(#revArea)" />
                <Area type="monotone" dataKey="profit" stroke="#f5d0fe" strokeWidth={2} fill="url(#profArea)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cashflow gauge */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-cyan-500/10 to-transparent p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Cashflow</p>
          <div className="relative mt-2 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="65%" outerRadius="100%" data={cashGauge} startAngle={210} endAngle={-30}>
                <defs>
                  <linearGradient id="cashGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#67e8f9" />
                    <stop offset="100%" stopColor="#a78bfa" />
                  </linearGradient>
                </defs>
                <RadialBar background={{ fill: 'rgba(255,255,255,0.05)' }} dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-black tracking-tighter">72</span>
              <span className="text-[8px] uppercase tracking-wider text-emerald-300">Healthy</span>
            </div>
          </div>
          <p className="mt-1 text-center text-[10px] text-white/50">94 days runway</p>
        </div>

        {/* Revenue mix donut */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-fuchsia-500/10 to-transparent p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Mix</p>
          <div className="relative mt-2 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={revenueMix} dataKey="value" innerRadius={28} outerRadius={42} paddingAngle={3} stroke="none">
                  {revenueMix.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 flex flex-wrap justify-center gap-1.5 text-[9px]">
            {revenueMix.map(r => (
              <span key={r.name} className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: r.color }} />
                <span className="text-white/60">{r.name}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Top expenses - horizontal */}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
        <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Where money flows</p>
        <p className="mt-1 text-base font-bold tracking-tight">Top expenses · YTD</p>
        <div className="mt-4 h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={expenses} layout="vertical" margin={{ left: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.6)' }} width={110} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <Bar dataKey="amount" radius={[8, 8, 8, 8]}>
                {expenses.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Deduction tips - magazine pull-quote */}
      <div className="relative overflow-hidden rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-500/15 via-orange-500/5 to-transparent p-6">
        <div className="absolute right-4 top-4">
          <Lightbulb className="h-5 w-5 text-amber-300" />
        </div>
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-amber-200/80">Editor's note</p>
        <h3 className="mt-2 text-lg font-bold leading-snug tracking-tight">Three deductions worth a conversation</h3>
        <div className="mt-4 space-y-3">
          {[
            { t: 'Home office', d: 'Internet, electricity, phone - pro-rated for work hours.' },
            { t: 'Vehicle logbook', d: 'A 12-week log this quarter is valid for 5 years.' },
            { t: 'Asset write-off', d: 'Equipment under $20k may be immediately deductible.' },
          ].map((tip, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-[11px] font-black text-amber-200">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="flex-1 border-l border-amber-300/20 pl-3">
                <p className="text-sm font-semibold">{tip.t}</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-white/60">{tip.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benchmarks - comparison bars */}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-cyan-300" />
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">Versus industry</p>
        </div>
        <p className="mt-1 text-base font-bold tracking-tight">How you compare</p>
        <div className="mt-5 space-y-5">
          {benchmarks.map(b => {
            const better = b.metric === 'Profit margin' ? b.you > b.industry : b.you < b.industry;
            return (
              <div key={b.metric}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/80">{b.metric}</span>
                  <span className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${better ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {better ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {better ? 'Above' : 'Below'}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[9px] font-bold uppercase text-white/40">You</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-400" style={{ width: `${(b.you / Math.max(b.you, b.industry)) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs font-bold tabular-nums">{b.you}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[9px] font-bold uppercase text-white/30">Avg</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                      <div className="h-full rounded-full bg-white/30" style={{ width: `${(b.industry / Math.max(b.you, b.industry)) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs tabular-nums text-white/60">{b.industry}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer signature */}
      <div className="flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.3em] text-white/30">
        <Sparkles className="h-3 w-3" />
        <span>Curated by your accountant</span>
      </div>
    </div>
  );
}
